import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import { useActivityLog } from '../../../hooks/useActivityLog';
import '../ProductManagement/ProductForm.css';
import './SuppliersList.css';

const SuppliersList = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const logActivity = useActivityLog();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    supplierName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    paymentTerms: '',
    creditLimit: '',
    currency: 'USD',
    leadTimeDays: '',
    minimumOrderValue: '',
    notes: '',
    isActive: true,
    isPreferred: false
  });

  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    preferred: 'all',
    country: 'all'
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await inventoryApi.getSuppliers(organization);

      // Handle both array and {data: []} responses
      const suppliersData = Array.isArray(response) ? response : (response.data || []);

      // Handle both snake_case and camelCase
      const normalizedSuppliers = suppliersData.map(supplier => ({
        id: supplier.id,
        supplierName: supplier.supplier_name || supplier.supplierName || '',
        contactPerson: supplier.contact_person || supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        state: supplier.state || '',
        postalCode: supplier.postal_code || supplier.postalCode || '',
        country: supplier.country || '',
        taxId: supplier.tax_id || supplier.taxId || '',
        paymentTerms: supplier.payment_terms || supplier.paymentTerms || '',
        creditLimit: supplier.credit_limit || supplier.creditLimit || 0,
        currency: supplier.currency || 'USD',
        leadTimeDays: supplier.lead_time_days || supplier.leadTimeDays || 0,
        minimumOrderValue: supplier.minimum_order_value || supplier.minimumOrderValue || 0,
        notes: supplier.notes || '',
        isActive: supplier.is_active !== undefined ? supplier.is_active : supplier.isActive !== undefined ? supplier.isActive : true,
        isPreferred: supplier.is_preferred !== undefined ? supplier.is_preferred : supplier.isPreferred !== undefined ? supplier.isPreferred : false,
        createdAt: supplier.created_at || supplier.createdAt,
        updatedAt: supplier.updated_at || supplier.updatedAt
      }));

      setSuppliers(normalizedSuppliers);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // Convert to snake_case for backend
      const supplierData = {
        supplier_name: formData.supplierName,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        tax_id: formData.taxId,
        payment_terms: formData.paymentTerms,
        credit_limit: parseFloat(formData.creditLimit) || 0,
        currency: formData.currency,
        lead_time_days: parseInt(formData.leadTimeDays) || 0,
        minimum_order_value: parseFloat(formData.minimumOrderValue) || 0,
        notes: formData.notes,
        is_active: formData.isActive,
        is_preferred: formData.isPreferred,
        organization
      };

      if (editingSupplier) {
        await inventoryApi.updateSupplier(editingSupplier.id, supplierData);
        logActivity(organization, 'SUPPLIER_UPDATED', { supplier_name: supplierData.supplier_name });
      } else {
        await inventoryApi.createSupplier(supplierData);
        logActivity(organization, 'SUPPLIER_CREATED', { supplier_name: supplierData.supplier_name });
      }

      // Reload suppliers
      await loadSuppliers();

      // Close modal and reset form
      setShowModal(false);
      setEditingSupplier(null);
      resetForm();
    } catch (err) {
      console.error('Error saving supplier:', err);
      setError('Failed to save supplier: ' + err.message);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      supplierName: supplier.supplierName || '',
      contactPerson: supplier.contactPerson || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      state: supplier.state || '',
      postalCode: supplier.postalCode || '',
      country: supplier.country || '',
      taxId: supplier.taxId || '',
      paymentTerms: supplier.paymentTerms || '',
      creditLimit: supplier.creditLimit || '',
      currency: supplier.currency || 'USD',
      leadTimeDays: supplier.leadTimeDays || '',
      minimumOrderValue: supplier.minimumOrderValue || '',
      notes: supplier.notes || '',
      isActive: supplier.isActive !== undefined ? supplier.isActive : true,
      isPreferred: supplier.isPreferred !== undefined ? supplier.isPreferred : false
    });
    setShowModal(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      await inventoryApi.deleteSupplier(supplierId);
      await loadSuppliers();
      logActivity(organization, 'SUPPLIER_DELETED', { supplierId });
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError('Failed to delete supplier: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      supplierName: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      taxId: '',
      paymentTerms: '',
      creditLimit: '',
      currency: 'USD',
      leadTimeDays: '',
      minimumOrderValue: '',
      notes: '',
      isActive: true,
      isPreferred: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const applyFilters = () => {
    let filtered = [...suppliers];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        (s.supplierName && s.supplierName.toLowerCase().includes(search)) ||
        (s.contactPerson && s.contactPerson.toLowerCase().includes(search)) ||
        (s.email && s.email.toLowerCase().includes(search)) ||
        (s.phone && s.phone.includes(search)) ||
        (s.country && s.country.toLowerCase().includes(search))
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(s => {
        const isActive = s.isActive !== undefined ? s.isActive : s.is_active !== undefined ? s.is_active : true;
        return filters.status === 'active' ? isActive : !isActive;
      });
    }

    // Preferred filter
    if (filters.preferred !== 'all') {
      filtered = filtered.filter(s => {
        const isPreferred = s.isPreferred !== undefined ? s.isPreferred : s.is_preferred !== undefined ? s.is_preferred : false;
        return filters.preferred === 'yes' ? isPreferred : !isPreferred;
      });
    }

    // Country filter
    if (filters.country !== 'all') {
      filtered = filtered.filter(s => s.country === filters.country);
    }

    return filtered;
  };

  const getUniqueCountries = () => {
    const countries = suppliers.map(s => s.country).filter(Boolean);
    return [...new Set(countries)].sort();
  };

  const filteredSuppliers = applyFilters();

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.suppliers_loading')}
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.suppliers_management_title')}</h2>
        <div className="pm-header-actions">
          <button
            className="pm-btn-primary"
            onClick={() => {
              setEditingSupplier(null);
              resetForm();
              setShowModal(true);
            }}
          >
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_supplier_button')}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true" style={{ fontSize: '1.25rem' }}>❌</span>
          <div>
            <strong>{t('inventory.error_label')}</strong> {error}
          </div>
          <button onClick={() => setError(null)} className="pm-btn-secondary" style={{ marginLeft: 'auto' }}>
            ✕
          </button>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <input
            type="search"
            placeholder={t('inventory.search_suppliers_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filter-wrap">
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">{t('inventory.filter_all_status')}</option>
            <option value="active">{t('inventory.filter_active')}</option>
            <option value="inactive">{t('inventory.filter_inactive')}</option>
          </select>

          <select
            value={filters.preferred}
            onChange={(e) => setFilters(prev => ({ ...prev, preferred: e.target.value }))}
          >
            <option value="all">{t('inventory.filter_all_suppliers')}</option>
            <option value="yes">{t('inventory.filter_preferred')}</option>
            <option value="no">{t('inventory.filter_standard')}</option>
          </select>

          <select
            value={filters.country}
            onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
          >
            <option value="all">{t('inventory.filter_all_countries')}</option>
            {getUniqueCountries().map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="pm-kpi-grid pm-kpi-grid-small">
        <div className="pm-kpi-card">
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{suppliers.length}</div>
            <div className="pm-kpi-label">{t('inventory.stat_total_suppliers')}</div>
          </div>
        </div>
        <div className="pm-kpi-card">
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{suppliers.filter(s => s.isActive || s.is_active).length}</div>
            <div className="pm-kpi-label">{t('inventory.stat_active')}</div>
          </div>
        </div>
        <div className="pm-kpi-card">
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{suppliers.filter(s => s.isPreferred || s.is_preferred).length}</div>
            <div className="pm-kpi-label">{t('inventory.stat_preferred')}</div>
          </div>
        </div>
        <div className="pm-kpi-card">
          <div className="pm-kpi-content">
            <div className="pm-kpi-value">{filteredSuppliers.length}</div>
            <div className="pm-kpi-label">{t('inventory.stat_filtered_results')}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredSuppliers.length === 0 ? (
        <div className="pm-empty-state">
          <div className="pm-empty-state-icon" aria-hidden="true">🤝</div>
          <h3>{t('inventory.no_suppliers_found')}</h3>
          <button
            className="pm-btn-primary"
            onClick={() => {
              setEditingSupplier(null);
              resetForm();
              setShowModal(true);
            }}
          >
            <span aria-hidden="true">➕</span>
            {t('inventory.add_supplier_button')}
          </button>
        </div>
      ) : (
        <div className="pm-table-wrap">
          <table className="pm-table" role="grid">
            <thead>
              <tr>
                <th>{t('inventory.table_supplier_name')}</th>
                <th>{t('inventory.table_contact_person')}</th>
                <th>{t('inventory.table_email')}</th>
                <th>{t('inventory.table_phone')}</th>
                <th>{t('inventory.table_location')}</th>
                <th>{t('inventory.table_payment_terms')}</th>
                <th>{t('inventory.table_lead_time')}</th>
                <th>{t('inventory.table_status')}</th>
                <th style={{ width: '100px' }}>{t('inventory.table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSuppliers.map(supplier => (
                <tr key={supplier.id}>
                  <td>
                    <div className="pm-supplier-name">
                      {supplier.supplierName}
                      {(supplier.isPreferred || supplier.is_preferred) && (
                        <span className="pm-badge-preferred">{t('inventory.badge_preferred')}</span>
                      )}
                    </div>
                  </td>
                  <td>{supplier.contactPerson || '-'}</td>
                  <td>{supplier.email || '-'}</td>
                  <td>{supplier.phone || '-'}</td>
                  <td>
                    {supplier.city && supplier.country ? (
                      <div className="pm-location-text">
                        {supplier.city}, {supplier.country}
                      </div>
                    ) : (
                      supplier.country || '-'
                    )}
                  </td>
                  <td>{supplier.paymentTerms || '-'}</td>
                  <td>
                    {supplier.leadTimeDays || supplier.lead_time_days ? (
                      `${supplier.leadTimeDays || supplier.lead_time_days} ${t('inventory.days_suffix')}`
                    ) : '-'}
                  </td>
                  <td>
                    <span className={`pm-status-badge ${(supplier.isActive || supplier.is_active) ? 'active' : 'inactive'}`}>
                      {(supplier.isActive || supplier.is_active) ? t('inventory.status_active') : t('inventory.status_inactive')}
                    </span>
                  </td>
                  <td>
                    <div className="pm-row-actions">
                      <button
                        className="pm-row-btn edit"
                        onClick={() => handleEdit(supplier)}
                        title={t('inventory.tooltip_edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="pm-row-btn delete"
                        onClick={() => handleDelete(supplier.id)}
                        title={t('inventory.tooltip_delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="pm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="pm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingSupplier ? t('inventory.edit_supplier_title') : t('inventory.add_supplier_title')}</h2>
              <button className="pm-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="pm-modal-body">
                <div className="pm-subsection-title">{t('inventory.section_basic_info')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.supplier_name_label')} <span className="pm-required">*</span></label>
                    <input
                      type="text"
                      name="supplierName"
                      value={formData.supplierName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.contact_person_label')}</label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.email_label')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.phone_label')}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.section_address')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group full-width">
                    <label>{t('inventory.address_label')}</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.city_label')}</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.state_label')}</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.postal_code_label')}</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.country_label')}</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.tax_id_label')}</label>
                    <input
                      type="text"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.section_payment_terms')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label>{t('inventory.payment_terms_label')}</label>
                    <input
                      type="text"
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleInputChange}
                      placeholder={t('inventory.payment_terms_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.credit_limit_label')}</label>
                    <input
                      type="number"
                      name="creditLimit"
                      value={formData.creditLimit}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.currency_label')}</label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                      <option value="JPY">JPY</option>
                      <option value="CNY">CNY</option>
                    </select>
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.lead_time_label')}</label>
                    <input
                      type="number"
                      name="leadTimeDays"
                      value={formData.leadTimeDays}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>

                  <div className="pm-form-group">
                    <label>{t('inventory.min_order_value_label')}</label>
                    <input
                      type="number"
                      name="minimumOrderValue"
                      value={formData.minimumOrderValue}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div className="pm-subsection-title">{t('inventory.section_additional_info')}</div>
                <div className="pm-form-grid">
                  <div className="pm-form-group full-width">
                    <label>{t('inventory.notes_label')}</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>

                  <div className="pm-form-group full-width">
                    <div className="pm-toggle-row">
                      <label className="pm-toggle-row-inline">
                        <input
                          type="checkbox"
                          role="switch"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleInputChange}
                        />
                        {t('inventory.active_label')}
                      </label>

                      <label className="pm-toggle-row-inline">
                        <input
                          type="checkbox"
                          role="switch"
                          name="isPreferred"
                          checked={formData.isPreferred}
                          onChange={handleInputChange}
                        />
                        {t('inventory.preferred_label')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button
                  type="button"
                  className="pm-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {t('inventory.cancel_button')}
                </button>
                <button type="submit" className="pm-btn-primary">
                  {editingSupplier ? t('inventory.save_button') : t('inventory.save_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersList;
